import { useState, useMemo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// ─── CONFIG ───────────────────────────────────────────────────
// const API_BASE     = "http://localhost:5000/api";
const API_BASE = "http://192.168.0.57:5000/api";
const TAX_RATE     = 0.18;
const ADV_PCT      = 0.30;
const NIGHT_CHARGE = 250;

// ─── MOCK FALLBACK ────────────────────────────────────────────
const mockCtx = {
  pickup: "Bhubaneswar Airport", dropoff: "Bhubaneswar Airport",
  pickupDate: "2025-05-10", pickupTime: "10:00",
  returnDate: "2025-05-13",  returnTime: "10:00",
  driverAge: "26-35", days: 3,
  selectedCar: {
    car_id: 2, car_name: "Innova Crysta", car_brand: "Toyota",
    cat_name: "MPV", group_name: "7 Seater",
    car_passgrno: 7, car_luggno: 4,
    car_transmission: "Automatic", car_airconditionr: 1,
    car_image: "", base_price: 2800,
  },
  selectedExtras: [
    { id: 1, name: "GPS Navigation", icon: "🗺️", price: 150, unit: "per day", selected: true },
  ],
  selectedInsurance: { ins_id: 1, ins_item: "Basic Protection", ins_chargeperday: 200 },
  // personalInfo can come in EITHER key format from Step 3 — we normalise below
  personalInfo: {
    title: "Mr", firstName: "Rahul", lastName: "Sharma",
    mobile: "+91 98765 43210", email: "rahul@example.com",
    address: "42 MG Road", city: "Bhubaneswar", postCode: "751001",
    country: "India", dlNumber: "OD0420230012345", comments: "",
  },
};

// ─── KEY NORMALISER ──────────────────────────────────────────
// BookingStep3 uses string-key form: { "First Name": "Rahul", "Mobile No": "+91…" }
// BookingStep4 and EditModal use camelCase: { firstName, mobile }
// This function converts EITHER format to camelCase so the whole page
// works correctly regardless of which Step 3 variant called it.
function normalisePersonalInfo(raw = {}) {
  if (!raw) return {};
  return {
    title:     raw.title      || raw.selTitle || "Mr",
    firstName: raw.firstName  || raw["First Name"]  || "",
    lastName:  raw.lastName   || raw["Last Name"]   || "",
    mobile:    raw.mobile     || raw["Mobile No"]   || raw.phone || "",
    email:     raw.email      || raw.Email          || raw["Email"] || "",
    address:   raw.address    || raw.Address        || raw["Address"] || "",
    city:      raw.city       || raw.City           || raw["City"] || "",
    postCode:  raw.postCode   || raw["Post Code"]   || raw.postcode || "",
    country:   raw.country    || raw.Country        || "India (+91)",
    dlNumber:  raw.dlNumber   || raw["DL Number"]   || raw.dl_number || "",
    dlExpiry:  raw.dlExpiry   || raw["DL Expiry"]   || raw.dl_expirydate || "",
    comments:  raw.comments   || raw.Comments       || "",
    driverAge: raw.driverAge  || "",
    prevBookingNum: raw.prevBookingNum || "",
  };
}

// ─── HELPERS ──────────────────────────────────────────────────
const fmtPrice = n =>
  (!n && n !== 0) ? "₹0" : "₹" + Math.round(Number(n)).toLocaleString("en-IN");

const isNightTime = t => {
  const h = parseInt(t?.split(":")[0] ?? "0");
  return h >= 22 || h < 7;
};

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

function calcTotals(ctx) {
  const days   = ctx.days || 1;
  const base   = (ctx.selectedCar?.base_price || 0) * days;
  const night  = (isNightTime(ctx.pickupTime) ? NIGHT_CHARGE : 0)
               + (isNightTime(ctx.returnTime)  ? NIGHT_CHARGE : 0);
  // const extras = (ctx.selectedExtras || [])
  //   .filter(e => e.selected || e.id)
  //   .reduce((s, e) => s + (e.unit === "per day" ? e.price * days : e.price), 0);
  const extras = (ctx.selectedExtras || [])
  .filter(e => !e.isFree)
  .reduce((s, e) => s + (e.unit === "per day" ? e.price * days : e.price), 0);


  // const ins    = parseFloat(ctx.selectedInsurance?.ins_chargeperday || 0) * days;
    const insArray = Array.isArray(ctx.selectedInsurance)
    ? ctx.selectedInsurance
    : ctx.selectedInsurance ? [ctx.selectedInsurance] : [];
    const ins = insArray.reduce(
    (sum, i) => sum + parseFloat(i.ins_chargeperday || 0) * days,
    0
    );

  const sub    = base + night + extras + ins;
  const tax    = sub * TAX_RATE;
  const grand  = sub + tax;
  const advance  = grand * ADV_PCT;
  const balance  = grand - advance;
  return { base, night, extras, ins, sub, tax, grand, advance, balance, days };
}

function detectCardType(num) {
  const n = num.replace(/\s/g, "");
  if (/^4/.test(n))      return "visa";
  if (/^5[1-5]/.test(n)) return "mastercard";
  if (/^3[47]/.test(n))  return "amex";
  return "unknown";
}

// ─── CSS ──────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --gold:#C9A84C; --gold-light:#E8C97A;
    --dark:#0A0A0F; --dark2:#111118; --dark3:#1A1A24;
    --surface:#16161F; --border:rgba(201,168,76,0.18);
    --text:#E8E8F0; --muted:#7A7A9A; --white:#FFFFFF;
    --green:#1D9E75; --red:#E24B4A;
    --radius:12px;
  }
  *{margin:0;padding:0;box-sizing:border-box;}
  html{scroll-behavior:smooth;}
  body{font-family:'DM Sans',sans-serif;background:var(--dark);color:var(--text);overflow-x:hidden;}

  /* ── NAV ── */
  .nav{
    position:fixed;top:0;left:0;right:0;z-index:1000;
    display:flex;align-items:center;justify-content:space-between;
    padding:0 4%;height:64px;
    background:rgba(10,10,15,0.97);backdrop-filter:blur(18px);
    border-bottom:1px solid var(--border);
  }
  .nav-logo{font-family:'Playfair Display',serif;font-size:1.45rem;color:var(--gold);cursor:pointer;white-space:nowrap;}
  .nav-logo span{color:var(--white);}
  .nav-steps{display:flex;align-items:center;overflow-x:auto;gap:0;}
  .nav-step{display:flex;align-items:center;gap:.4rem;padding:.3rem .65rem;font-size:.73rem;font-weight:600;letter-spacing:.4px;border-radius:20px;color:var(--muted);white-space:nowrap;}
  .nav-step.done{color:var(--green);}
  .nav-step.active{color:var(--gold);background:rgba(201,168,76,.1);}
  .nav-step-num{width:19px;height:19px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.65rem;font-weight:700;border:1.5px solid currentColor;flex-shrink:0;}
  .nav-step-arrow{color:var(--muted);font-size:.68rem;padding:0 .1rem;}

  /* ── SUMMARY BAR ── */
  .summary-bar{
    position:sticky;top:64px;z-index:900;
    background:var(--dark2);border-bottom:1px solid var(--border);
    padding:.7rem 4%;display:flex;align-items:center;gap:1rem;flex-wrap:wrap;
  }
  .sb-item{display:flex;align-items:center;gap:.4rem;font-size:.78rem;min-width:0;}
  .sb-label{color:var(--muted);font-size:.65rem;letter-spacing:1px;text-transform:uppercase;display:block;white-space:nowrap;}
  .sb-val{color:var(--text);font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:160px;}
  .sb-divider{width:1px;height:24px;background:var(--border);flex-shrink:0;}
  .sb-days{background:rgba(201,168,76,.12);border:1px solid var(--gold);color:var(--gold);font-size:.73rem;font-weight:700;padding:.25rem .7rem;border-radius:20px;white-space:nowrap;}
  .sb-edit{margin-left:auto;background:transparent;color:var(--gold);border:1px solid var(--gold);font-size:.73rem;font-weight:600;padding:.3rem .85rem;border-radius:8px;cursor:pointer;transition:all .2s;white-space:nowrap;font-family:inherit;flex-shrink:0;}
  .sb-edit:hover{background:var(--gold);color:var(--dark);}

  /* ── PAGE WRAP ── */
  .step4-wrap{margin-top:128px;} /* nav(64) + bar(64) */
  .step4-header{padding:1.5rem 4% .75rem;}
  .step4-header h1{font-family:'Playfair Display',serif;font-size:clamp(1.5rem,4vw,2rem);color:var(--white);}
  .step4-header p{font-size:.85rem;color:var(--muted);margin-top:.3rem;}

  /* ── TWO-COL LAYOUT ── */
  .step4-layout{
    display:grid;
    grid-template-columns:1fr 360px;
    gap:0;
    align-items:start;
  }

  /* ── LEFT COLUMN ── */
  .pay-left{padding:0 1.5rem 3rem;}

  /* ── CARD / SECTION ── */
  .s-card{background:var(--dark2);border:1px solid var(--border);border-radius:16px;padding:1.25rem;margin-bottom:1.25rem;}
  .s-title{
    font-size:.65rem;letter-spacing:2.5px;text-transform:uppercase;
    color:var(--gold);font-weight:700;margin-bottom:1.1rem;
    display:flex;align-items:center;gap:.55rem;
  }
  .s-title::after{content:'';flex:1;height:1px;background:var(--border);}

  /* ── PERSONAL INFO GRID ── */
  .pi-grid{display:grid;grid-template-columns:1fr 1fr;gap:.4rem 1.25rem;}
  .pi-row{display:flex;flex-direction:column;padding:.32rem 0;border-bottom:1px solid rgba(201,168,76,.07);}
  .pi-row:last-child{border-bottom:none;}
  .pi-label{font-size:.63rem;letter-spacing:1px;text-transform:uppercase;color:var(--muted);margin-bottom:.15rem;}
  .pi-val{font-size:.85rem;color:var(--text);font-weight:500;word-break:break-word;}
  .pi-val.empty{color:var(--muted);font-style:italic;}
  .modify-btn{
    display:flex;align-items:center;gap:.35rem;
    background:transparent;color:var(--gold);border:1px solid var(--gold);
    font-size:.75rem;font-weight:600;padding:.35rem .85rem;border-radius:8px;
    cursor:pointer;transition:all .2s;font-family:inherit;margin-left:auto;flex-shrink:0;
  }
  .modify-btn:hover{background:rgba(201,168,76,.1);}

  /* ── PAYMENT TABS ── */
  .pay-tabs{display:flex;gap:.5rem;margin-bottom:1.25rem;flex-wrap:wrap;}
  .pay-tab{
    flex:1;min-width:80px;
    display:flex;flex-direction:column;align-items:center;gap:.35rem;
    padding:.75rem .5rem;border:1.5px solid var(--border);border-radius:12px;
    background:var(--dark3);cursor:pointer;transition:all .2s;
  }
  .pay-tab:hover{border-color:var(--gold);}
  .pay-tab.active{border-color:var(--gold);background:rgba(201,168,76,.08);}
  .pay-tab-icon{font-size:1.35rem;}
  .pay-tab-label{font-size:.68rem;color:var(--muted);font-weight:600;letter-spacing:.4px;text-transform:uppercase;text-align:center;}
  .pay-tab.active .pay-tab-label{color:var(--gold);}

  /* ── CARD PREVIEW ── */
  .card-preview{
    background:linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%);
    border:1px solid rgba(201,168,76,.25);border-radius:16px;
    padding:1.25rem 1.5rem;margin-bottom:1.25rem;
    position:relative;overflow:hidden;
    min-height:160px;display:flex;flex-direction:column;justify-content:space-between;
  }
  .card-preview::before{content:'';position:absolute;top:-40px;right:-40px;width:160px;height:160px;border-radius:50%;background:rgba(201,168,76,.07);}
  .card-preview::after {content:'';position:absolute;bottom:-50px;left:20px; width:180px;height:180px;border-radius:50%;background:rgba(201,168,76,.05);}
  .card-chip{width:34px;height:25px;background:linear-gradient(135deg,#C9A84C,#E8C97A);border-radius:4px;margin-bottom:.4rem;position:relative;z-index:1;}
  .card-number-display{font-size:clamp(.9rem,3vw,1.15rem);letter-spacing:.15em;color:rgba(255,255,255,.85);font-weight:500;position:relative;z-index:1;margin:auto 0;font-family:'DM Sans',monospace;}
  .card-bottom{display:flex;justify-content:space-between;align-items:flex-end;position:relative;z-index:1;flex-wrap:wrap;gap:.5rem;}
  .card-holder-label{font-size:.58rem;color:rgba(255,255,255,.4);letter-spacing:1.5px;text-transform:uppercase;margin-bottom:.15rem;}
  .card-holder-name{font-size:.82rem;color:rgba(255,255,255,.85);font-weight:500;text-transform:uppercase;letter-spacing:.06em;}

  /* ── FORM INPUTS ── */
  .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:.8rem;margin-bottom:.8rem;}
  .form-grid.full{grid-template-columns:1fr;}
  .form-grid.cols3{grid-template-columns:1fr 1fr 1fr;}
  .form-group{display:flex;flex-direction:column;gap:.32rem;}
  .form-label{font-size:.67rem;letter-spacing:.8px;text-transform:uppercase;color:var(--muted);font-weight:600;}
  .form-input{
    background:var(--dark3);border:1px solid var(--border);color:var(--text);
    font-family:'DM Sans',sans-serif;font-size:.85rem;padding:.6rem .85rem;
    border-radius:10px;outline:none;transition:border .2s;width:100%;
  }
  .form-input:focus{border-color:var(--gold);}
  .form-input::placeholder{color:var(--muted);}
  .form-input.mono{font-family:'DM Sans',monospace;letter-spacing:.1em;}
  .form-input.error{border-color:var(--red);}
  .form-select{
    background:var(--dark3);border:1px solid var(--border);color:var(--text);
    font-family:'DM Sans',sans-serif;font-size:.85rem;padding:.6rem .85rem;
    border-radius:10px;outline:none;transition:border .2s;width:100%;cursor:pointer;
  }
  .form-select:focus{border-color:var(--gold);}
  .form-select option{background:#1f1f26;}
  .field-err{font-size:.7rem;color:var(--red);}

  /* ── UPI ── */
  .upi-row{display:flex;gap:.7rem;margin-bottom:1.1rem;flex-wrap:wrap;}
  .upi-app{display:flex;flex-direction:column;align-items:center;gap:.35rem;padding:.65rem .85rem;border:1px solid var(--border);border-radius:12px;background:var(--dark3);cursor:pointer;transition:all .2s;min-width:72px;}
  .upi-app:hover{border-color:var(--gold);}
  .upi-app.active{border-color:var(--gold);background:rgba(201,168,76,.08);}
  .upi-app-icon{font-size:1.3rem;}
  .upi-app-name{font-size:.68rem;color:var(--muted);font-weight:600;}
  .upi-app.active .upi-app-name{color:var(--gold);}

  /* ── NET BANKING ── */
  .bank-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:.5rem;margin-bottom:1.1rem;}
  .bank-item{padding:.55rem .65rem;border:1px solid var(--border);border-radius:10px;background:var(--dark3);cursor:pointer;transition:all .2s;text-align:center;font-size:.78rem;color:var(--muted);font-weight:500;}
  .bank-item:hover{border-color:var(--gold);color:var(--text);}
  .bank-item.active{border-color:var(--gold);color:var(--gold);background:rgba(201,168,76,.08);}

  /* ── BOOKING REVIEW ── */
  .review-row{display:flex;justify-content:space-between;padding:.38rem 0;border-bottom:1px solid var(--border);font-size:.82rem;}
  .review-row:last-child{border-bottom:none;}
  .review-key{color:var(--muted);}
  .review-val{color:var(--text);font-weight:500;text-align:right;max-width:58%;word-break:break-word;}
  .extras-list{margin-top:.45rem;display:flex;flex-wrap:wrap;gap:.3rem;}
  .extra-pill{display:inline-flex;align-items:center;gap:.3rem;background:rgba(201,168,76,.1);border:1px solid rgba(201,168,76,.2);color:var(--gold);font-size:.72rem;font-weight:600;padding:.18rem .5rem;border-radius:20px;}

  /* ── RIGHT PANEL ── */
  .pay-right{
    background:var(--dark2);border-left:1px solid var(--border);
    padding:1.5rem 1.25rem;display:flex;flex-direction:column;gap:1.1rem;
    position:sticky;top:128px;
    height:calc(100vh - 128px);overflow-y:auto;
  }
  .pay-right::-webkit-scrollbar{width:3px;}
  .pay-right::-webkit-scrollbar-thumb{background:var(--border);border-radius:2px;}

  .bp-section{background:var(--surface);border:1px solid var(--border);border-radius:14px;padding:1.1rem;}
  .bp-heading{font-size:.65rem;letter-spacing:2px;text-transform:uppercase;color:var(--gold);font-weight:700;margin-bottom:.9rem;}
  .price-row{display:flex;justify-content:space-between;align-items:center;padding:.35rem 0;}
  .price-label{font-size:.8rem;color:var(--muted);}
  .price-val{font-size:.85rem;color:var(--text);font-weight:500;}
  .price-row.divider{border-top:1px solid var(--border);margin-top:.35rem;padding-top:.65rem;}
  .price-row.divider .price-label{color:var(--text);font-weight:600;}
  .price-row.divider .price-val{font-family:'Playfair Display',serif;font-size:1.25rem;color:var(--gold);}
  .price-row.grand{border-top:1px solid var(--border);margin-top:.3rem;padding-top:.6rem;}
  .price-row.grand .price-label{color:var(--text);font-weight:700;font-size:.9rem;}
  .price-row.grand .price-val{font-family:'Playfair Display',serif;font-size:1.45rem;color:var(--white);}

  .adv-highlight{
    background:linear-gradient(135deg,rgba(201,168,76,.12),rgba(201,168,76,.06));
    border:1px solid rgba(201,168,76,.3);border-radius:14px;
    padding:1rem 1.1rem;text-align:center;
  }
  .adv-label{font-size:.65rem;color:var(--muted);letter-spacing:1.5px;text-transform:uppercase;margin-bottom:.25rem;}
  .adv-amount{font-family:'Playfair Display',serif;font-size:1.8rem;color:var(--gold);line-height:1;}
  .adv-sub{font-size:.7rem;color:var(--muted);margin-top:.25rem;}
  .adv-balance{font-size:.74rem;color:var(--muted);margin-top:.45rem;padding-top:.45rem;border-top:1px solid rgba(201,168,76,.15);}
  .adv-balance strong{color:var(--text);}

  .security-badges{display:flex;justify-content:center;gap:.65rem;flex-wrap:wrap;}
  .sec-badge{display:flex;align-items:center;gap:.3rem;font-size:.7rem;color:var(--muted);}
  .sec-dot{width:5px;height:5px;border-radius:50%;background:var(--green);flex-shrink:0;}

  /* ── PAY BUTTON ── */
  .btn-pay{
    width:100%;background:var(--gold);color:var(--dark);
    font-family:'DM Sans',sans-serif;font-weight:700;font-size:1rem;
    padding:.9rem;border:none;border-radius:12px;cursor:pointer;
    transition:all .25s;margin-top:auto;
    display:flex;align-items:center;justify-content:center;gap:.5rem;
  }
  .btn-pay:hover:not(:disabled){background:var(--gold-light);transform:translateY(-2px);box-shadow:0 10px 28px rgba(201,168,76,.35);}
  .btn-pay:disabled{background:var(--border);color:var(--muted);cursor:not-allowed;transform:none;box-shadow:none;}
  .btn-pay.processing{background:var(--dark3);color:var(--gold);border:1px solid var(--gold);}
  @keyframes spin{to{transform:rotate(360deg);}}
  .spinner{width:17px;height:17px;border:2px solid rgba(201,168,76,.3);border-top-color:var(--gold);border-radius:50%;animation:spin .7s linear infinite;}
  .secure-note{font-size:.7rem;color:var(--muted);text-align:center;margin-top:.5rem;}

  /* ── PROCESSING OVERLAY ── */
  .processing-overlay{
    position:fixed;inset:0;background:rgba(10,10,15,.92);z-index:2000;
    display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1.25rem;
    backdrop-filter:blur(8px);
  }
  .processing-ring{width:72px;height:72px;border-radius:50%;border:3px solid rgba(201,168,76,.2);border-top-color:var(--gold);animation:spin .9s linear infinite;}
  .processing-text{font-family:'Playfair Display',serif;font-size:1.3rem;color:var(--white);}
  .processing-sub{font-size:.82rem;color:var(--muted);}

  /* ── COUPON ── */
  .coupon-row{display:flex;gap:.55rem;}
  .coupon-input{flex:1;background:var(--dark3);border:1px solid var(--border);color:var(--text);font-family:'DM Sans',sans-serif;font-size:.85rem;padding:.58rem .85rem;border-radius:10px;outline:none;transition:border .2s;min-width:0;}
  .coupon-input:focus{border-color:var(--gold);}
  .coupon-input::placeholder{color:var(--muted);}
  .coupon-btn{background:transparent;border:1px solid var(--gold);color:var(--gold);font-family:'DM Sans',sans-serif;font-size:.8rem;font-weight:700;padding:.58rem 1rem;border-radius:10px;cursor:pointer;transition:all .2s;white-space:nowrap;}
  .coupon-btn:hover:not(:disabled){background:var(--gold);color:var(--dark);}
  .coupon-btn:disabled{opacity:.5;cursor:not-allowed;}
  .coupon-success{font-size:.75rem;color:var(--green);margin-top:.35rem;display:flex;align-items:center;gap:.3rem;}
  .coupon-error{font-size:.75rem;color:var(--red);margin-top:.35rem;}
  .coupon-loading{font-size:.75rem;color:var(--muted);margin-top:.35rem;}

  /* ── INFO NOTICE ── */
  .info-notice{background:rgba(29,158,117,.08);border:1px solid rgba(29,158,117,.25);border-radius:10px;padding:.65rem .9rem;font-size:.75rem;color:#1D9E75;display:flex;align-items:flex-start;gap:.45rem;}

  /* ── EDIT PERSONAL MODAL ── */
  .modal-overlay{
    position:fixed;inset:0;background:rgba(0,0,0,.78);z-index:3000;
    display:flex;align-items:center;justify-content:center;
    padding:1rem;backdrop-filter:blur(5px);
    animation:fadeIn .2s ease;
  }
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  .edit-modal{
    background:var(--dark2);border:1px solid var(--border);border-radius:20px;
    width:100%;max-width:600px;max-height:90vh;overflow-y:auto;
    animation:slideUp .25s ease;
  }
  @keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
  .edit-modal::-webkit-scrollbar{width:3px;}
  .edit-modal::-webkit-scrollbar-thumb{background:var(--border);}
  .modal-header{
    position:sticky;top:0;background:var(--dark2);
    padding:1.25rem 1.5rem;border-bottom:1px solid var(--border);
    display:flex;align-items:center;justify-content:space-between;z-index:1;
  }
  .modal-header h2{font-family:'Playfair Display',serif;font-size:1.2rem;color:var(--white);}
  .modal-close-btn{
    background:var(--surface);border:1px solid var(--border);color:var(--muted);
    width:32px;height:32px;border-radius:50%;cursor:pointer;font-size:.95rem;
    display:flex;align-items:center;justify-content:center;transition:all .2s;flex-shrink:0;
  }
  .modal-close-btn:hover{border-color:var(--red);color:var(--red);}
  .modal-body{padding:1.25rem 1.5rem;}
  .modal-sec-label{
    font-size:.65rem;letter-spacing:2px;text-transform:uppercase;
    color:var(--gold);font-weight:700;margin-bottom:.8rem;margin-top:1.1rem;
    display:flex;align-items:center;gap:.5rem;
  }
  .modal-sec-label::after{content:'';flex:1;height:1px;background:var(--border);}
  .modal-sec-label:first-child{margin-top:0;}
  .modal-footer{
    padding:.9rem 1.5rem;border-top:1px solid var(--border);
    display:flex;gap:.65rem;justify-content:flex-end;flex-wrap:wrap;
  }
  .btn-save{background:var(--gold);color:var(--dark);font-weight:700;font-size:.88rem;padding:.6rem 1.6rem;border:none;border-radius:10px;cursor:pointer;font-family:inherit;transition:all .2s;}
  .btn-save:hover{background:var(--gold-light);}
  .btn-cancel{background:transparent;color:var(--muted);border:1px solid var(--border);font-size:.88rem;padding:.6rem 1.1rem;border-radius:10px;cursor:pointer;font-family:inherit;transition:all .2s;}
  .btn-cancel:hover{border-color:var(--gold);color:var(--gold);}

  /* ───────────── RESPONSIVE ───────────── */

  /* Tablet: stack layout, keep summary bar wrapping */
  @media(max-width:1000px){
    .step4-layout{grid-template-columns:1fr;}
    .pay-right{
      position:relative;top:0;height:auto;
      border-left:none;border-top:1px solid var(--border);
      padding:1.25rem;
    }
    .pay-left{padding:0 1rem 2rem;}
    .step4-header{padding:1.25rem 1rem .6rem;}
  }

  /* Tablet steps hidden */
  @media(max-width:860px){
    .nav-steps{display:none;}
    .pi-grid{grid-template-columns:1fr 1fr;}
  }

  /* Mobile: summary bar compact, single column everything */
  @media(max-width:640px){
    .nav{height:56px;padding:0 4%;}
    .nav-logo{font-size:1.25rem;}
    .step4-wrap{margin-top:112px;}
    .summary-bar{
      top:56px;
      padding:.55rem 4%;
      gap:.5rem;
    }
    /* Show only key chips on mobile */
    .sb-divider{display:none;}
    .sb-item:nth-child(n+4){display:none;}  /* hide from/to items, show only pickup + days */
    .sb-edit{padding:.28rem .7rem;font-size:.7rem;}

    .step4-header{padding:1rem 4% .5rem;}
    .step4-header h1{font-size:1.4rem;}
    .pay-left{padding:0 .75rem 2rem;}
    .s-card{padding:1rem;border-radius:14px;}

    /* Personal info single col on small screens */
    .pi-grid{grid-template-columns:1fr;}

    /* Payment tabs wrap nicely */
    .pay-tabs{gap:.4rem;}
    .pay-tab{min-width:calc(50% - .2rem);padding:.65rem .4rem;}

    /* Form grids collapse */
    .form-grid{grid-template-columns:1fr !important;}
    .form-grid.cols3{grid-template-columns:1fr 1fr !important;}

    /* Bank grid 2 cols */
    .bank-grid{grid-template-columns:1fr 1fr;}

    /* Card preview smaller text */
    .card-number-display{font-size:.95rem;letter-spacing:.1em;}

    /* Modal full screen on mobile */
    .modal-overlay{padding:.5rem;}
    .edit-modal{border-radius:16px;max-height:95vh;}
    .modal-body{padding:1rem 1.1rem;}
    .modal-footer{padding:.8rem 1.1rem;}

    /* Price panel */
    .pay-right{padding:1rem;}
    .adv-amount{font-size:1.55rem;}

    /* Coupon row */
    .coupon-row{flex-wrap:wrap;}
    .coupon-input{min-width:0;flex:1 1 60%;}
    .coupon-btn{flex:0 0 auto;}
  }

  /* Very small screens */
  @media(max-width:380px){
    .pay-tab{min-width:100%;}
    .upi-row{gap:.4rem;}
    .upi-app{min-width:60px;padding:.55rem .6rem;}
    .pi-grid{grid-template-columns:1fr;}
  }
`;

// ─── STEP NAV ─────────────────────────────────────────────────
const STEPS = ["Location", "Choose Car", "Reserve", "Payment", "Confirm"];

function StepNav({ onLogoClick }) {
  return (
    <nav className="nav">
      <div className="nav-logo" onClick={onLogoClick}>Orisnz<span>-Fleet</span></div>
      <div className="nav-steps">
        {STEPS.map((s, i) => (
          <div key={s} style={{ display: "flex", alignItems: "center" }}>
            <div className={`nav-step ${i < 3 ? "done" : i === 3 ? "active" : ""}`}>
              <div className="nav-step-num">{i < 3 ? "✓" : i + 1}</div>
              <span>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="nav-step-arrow">›</div>}
          </div>
        ))}
      </div>
    </nav>
  );
}

// ─── SUMMARY BAR ─────────────────────────────────────────────
function SummaryBar({ ctx, onEdit }) {
  return (
    <div className="summary-bar">
      <div className="sb-item">
        <span>📍</span>
        <div><div className="sb-label">Pickup</div><div className="sb-val">{ctx.pickup}</div></div>
      </div>
      <div className="sb-divider" />
      <div className="sb-item">
        <span>📅</span>
        <div><div className="sb-label">From</div><div className="sb-val">{fmtDate(ctx.pickupDate)} {ctx.pickupTime}</div></div>
      </div>
      <div className="sb-divider" />
      <div className="sb-item">
        <span>🔙</span>
        <div><div className="sb-label">Until</div><div className="sb-val">{fmtDate(ctx.returnDate)} {ctx.returnTime}</div></div>
      </div>
      <div className="sb-divider" />
      <div className="sb-days">{ctx.days} day{ctx.days !== 1 ? "s" : ""}</div>
      <button className="sb-edit" onClick={onEdit}>✏ Edit</button>
    </div>
  );
}

// ─── EDIT PERSONAL MODAL ─────────────────────────────────────
function EditPersonalModal({ info, onSave, onClose }) {
  const [form, setForm] = useState({ ...info });
  const overlayRef = useRef(null);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inp = (label, key, type = "text", ph = "") => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input className="form-input" type={type}
        placeholder={ph || label}
        value={form[key] || ""}
        onChange={e => set(key, e.target.value)} />
    </div>
  );

  const handleSave = () => {
    if (!form.firstName?.trim() || !form.lastName?.trim()) { alert("First and last name required."); return; }
    if (!form.mobile?.trim())                              { alert("Mobile number required.");        return; }
    if (!form.email?.trim() || !form.email.includes("@")) { alert("Valid email required.");          return; }
    onSave(form);
  };

  return (
    <div className="modal-overlay" ref={overlayRef}
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}>
      <div className="edit-modal">
        <div className="modal-header">
          <h2>Edit Personal Details</h2>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="modal-sec-label">Personal</div>
          <div className="form-grid" style={{ gridTemplateColumns: "100px 1fr 1fr", marginBottom: ".8rem" }}>
            <div className="form-group">
              <label className="form-label">Title</label>
              <select className="form-select" value={form.title || "Mr"} onChange={e => set("title", e.target.value)}>
                {["Mr","Mrs","Ms","Dr","Prof"].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            {inp("First Name", "firstName")}
            {inp("Last Name",  "lastName")}
          </div>

          <div className="modal-sec-label">Contact</div>
          <div className="form-grid" style={{ marginBottom: ".8rem" }}>
            {inp("Mobile", "mobile", "tel", "+91 XXXXX XXXXX")}
            {inp("Email",  "email",  "email", "you@example.com")}
          </div>

          <div className="modal-sec-label">Address</div>
          <div className="form-grid full" style={{ marginBottom: ".8rem" }}>
            {inp("Street Address", "address")}
          </div>
          <div className="form-grid" style={{ marginBottom: ".8rem" }}>
            {inp("City", "city")}
            {inp("Post Code", "postCode")}
          </div>
          <div className="form-grid full" style={{ marginBottom: ".8rem" }}>
            <div className="form-group">
              <label className="form-label">Country</label>
              <select className="form-select" value={form.country || "India"} onChange={e => set("country", e.target.value)}>
                {["India","USA","UK","UAE","Singapore","Australia","Canada","Other"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="modal-sec-label">Driving Licence</div>
          <div className="form-grid" style={{ marginBottom: ".8rem" }}>
            {inp("DL Number", "dlNumber", "text", "OD04XXXXXXXXXX")}
            <div className="form-group">
              <label className="form-label">Driver Age Range</label>
              <select className="form-select" value={form.driverAge || "26-35"} onChange={e => set("driverAge", e.target.value)}>
                {["21-25","26-35","36-45","46-60","61-70","71+"].map(a => <option key={a}>{a}</option>)}
              </select>
            </div>
          </div>

          <div className="modal-sec-label">Notes</div>
          <div className="form-group" style={{ marginBottom: ".5rem" }}>
            <label className="form-label">Special Requests</label>
            <textarea className="form-input" rows={3}
              placeholder="Any special requests…"
              value={form.comments || ""}
              onChange={e => set("comments", e.target.value)}
              style={{ resize: "vertical", lineHeight: 1.6 }} />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save"   onClick={handleSave}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

// ─── PERSONAL INFO DISPLAY CARD ───────────────────────────────
// ✅ Uses normalised camelCase keys — no more "Not provided" for valid data
function PersonalInfoCard({ info, onModify }) {
  const v = val => val?.toString().trim() || null;

  const fields = [
    { label: "Full Name",  value: v([info.title, info.firstName, info.lastName].filter(Boolean).join(" ")) },
    { label: "Mobile",     value: v(info.mobile) },
    { label: "Email",      value: v(info.email) },
    { label: "Address",    value: v([info.address, info.city, info.postCode].filter(Boolean).join(", ")) },
    { label: "Country",    value: v(info.country) },
    { label: "DL Number",  value: v(info.dlNumber) },
    { label: "Driver Age", value: v(info.driverAge ? `${info.driverAge} yrs` : null) },
    { label: "Comments",   value: v(info.comments) },
  ];

  return (
    <div className="s-card">
      <div style={{ display: "flex", alignItems: "center", marginBottom: "1.1rem", gap: ".5rem" }}>
        <div className="s-title" style={{ margin: 0, flex: 1, marginBottom: 0 }}>Personal Details</div>
        <button className="modify-btn" onClick={onModify}>✏️ Modify</button>
      </div>
      <div className="pi-grid">
        {fields.map(f => (
          <div className="pi-row" key={f.label}>
            <span className="pi-label">{f.label}</span>
            <span className={`pi-val${!f.value ? " empty" : ""}`}>{f.value || "Not provided"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CARD PREVIEW ─────────────────────────────────────────────
function CardPreview({ number, name, expiry, type }) {
  const masked  = number.replace(/\s/g, "").padEnd(16, "•");
  const parts   = [masked.slice(0,4), masked.slice(4,8), masked.slice(8,12), masked.slice(12,16)];
  const network = type === "amex" ? "AMEX" : type === "mastercard" ? "MC" : "VISA";
  return (
    <div className="card-preview">
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <div className="card-chip" />
        <span style={{ fontSize:".9rem", opacity:.7, zIndex:1, color:"rgba(255,255,255,.8)", fontWeight:600 }}>{network}</span>
      </div>
      <div className="card-number-display">{parts.join(" ")}</div>
      <div className="card-bottom">
        <div>
          <div className="card-holder-label">Card Holder</div>
          <div className="card-holder-name">{name || "YOUR NAME"}</div>
        </div>
        <div style={{ textAlign:"right" }}>
          <div className="card-holder-label">Expires</div>
          <div className="card-holder-name">{expiry || "MM/YY"}</div>
        </div>
      </div>
    </div>
  );
}

// ─── CARD FORM ────────────────────────────────────────────────
function CardForm({ onCardChange }) {
  const [card, setCard] = useState({ number:"", name:"", expiry:"", cvv:"", saveCard:false });
  const update = (k, v) => {
    const next = { ...card, [k]: v };
    setCard(next);
    const ok = next.number.replace(/\s/g,"").length >= 15 && next.name && next.expiry.length === 5 && next.cvv.length >= 3;
    onCardChange(ok ? "card" : null);
  };
  const fmt4   = v => v.replace(/\D/g,"").slice(0,16).replace(/(.{4})/g,"$1 ").trim();
  const fmtExp = v => { const d = v.replace(/\D/g,"").slice(0,4); return d.length >= 3 ? d.slice(0,2)+"/"+d.slice(2) : d; };
  const ct = detectCardType(card.number);
  return (
    <>
      <CardPreview number={card.number} name={card.name} expiry={card.expiry} type={ct} />
      <div className="form-grid full">
        <div className="form-group">
          <label className="form-label">Card Number</label>
          <input className="form-input mono" placeholder="1234 5678 9012 3456"
            value={card.number} maxLength={19}
            onChange={e => update("number", fmt4(e.target.value))} />
        </div>
      </div>
      <div className="form-grid full">
        <div className="form-group">
          <label className="form-label">Cardholder Name</label>
          <input className="form-input" placeholder="Name as on card"
            value={card.name} onChange={e => update("name", e.target.value.toUpperCase())} />
        </div>
      </div>
      <div className="form-grid cols3">
        <div className="form-group">
          <label className="form-label">Expiry</label>
          <input className="form-input mono" placeholder="MM/YY"
            value={card.expiry} maxLength={5}
            onChange={e => update("expiry", fmtExp(e.target.value))} />
        </div>
        <div className="form-group">
          <label className="form-label">CVV</label>
          <input className="form-input mono" placeholder="•••" type="password"
            value={card.cvv} maxLength={ct === "amex" ? 4 : 3}
            onChange={e => update("cvv", e.target.value.replace(/\D/g,""))} />
        </div>
        <div className="form-group">
          <label className="form-label" style={{ opacity:0 }}>_</label>
          <label style={{ display:"flex", alignItems:"center", gap:".45rem", height:"40px", cursor:"pointer", fontSize:".8rem", color:"var(--muted)" }}>
            <input type="checkbox" checked={card.saveCard}
              onChange={e => update("saveCard", e.target.checked)}
              style={{ accentColor:"var(--gold)", width:14, height:14 }} />
            Save card
          </label>
        </div>
      </div>
    </>
  );
}

// ─── UPI FORM ─────────────────────────────────────────────────
const UPI_APPS = [
  { id:"gpay",    icon:"🅶",  name:"GPay"    },
  { id:"phonepe", icon:"🟣",  name:"PhonePe" },
  { id:"paytm",   icon:"🔵",  name:"Paytm"   },
  { id:"bhim",    icon:"🏛️", name:"BHIM"    },
  { id:"other",   icon:"📱",  name:"Other"   },
];
function UpiForm({ onReady }) {
  const [sel, setSel] = useState("gpay");
  const [uid, setUid] = useState("");
  return (
    <div>
      <div className="upi-row">
        {UPI_APPS.map(a => (
          <div key={a.id} className={`upi-app ${sel === a.id ? "active":""}`} onClick={() => setSel(a.id)}>
            <span className="upi-app-icon">{a.icon}</span>
            <span className="upi-app-name">{a.name}</span>
          </div>
        ))}
      </div>
      <div className="form-group">
        <label className="form-label">UPI ID</label>
        <input className="form-input" placeholder="yourname@upi" value={uid}
          onChange={e => { setUid(e.target.value); onReady(e.target.value.includes("@") ? "upi" : null); }} />
      </div>
      <div className="info-notice" style={{ marginTop:"1rem" }}>
        <span>ℹ️</span>
        <span>A payment request will be sent to your UPI app. Approve within 5 minutes.</span>
      </div>
    </div>
  );
}

// ─── NET BANKING ──────────────────────────────────────────────
const BANKS = ["SBI","HDFC","ICICI","Axis","Kotak","PNB","BOB","Union","Canara","IDBI","Yes Bank","Other"];
function NetBankingForm({ onReady }) {
  const [sel, setSel] = useState(null);
  return (
    <div>
      <div className="bank-grid">
        {BANKS.map(b => (
          <div key={b} className={`bank-item ${sel===b?"active":""}`}
            onClick={() => { setSel(b); onReady("netbanking"); }}>{b}</div>
        ))}
      </div>
      <div className="info-notice"><span>ℹ️</span><span>You will be redirected to your bank's secure portal.</span></div>
    </div>
  );
}

// ─── WALLET FORM ─────────────────────────────────────────────
const WALLETS = [
  { id:"mobikwik",   name:"MobiKwik",     icon:"💙" },
  { id:"freecharge", name:"FreeCharge",   icon:"🟠" },
  { id:"airtel",     name:"Airtel Money", icon:"🔴" },
  { id:"amazon",     name:"Amazon Pay",   icon:"🟡" },
];
function WalletForm({ onReady }) {
  const [sel, setSel] = useState(null);
  return (
    <div>
      <div className="upi-row">
        {WALLETS.map(w => (
          <div key={w.id} className={`upi-app ${sel===w.id?"active":""}`}
            onClick={() => { setSel(w.id); onReady("wallet"); }}>
            <span className="upi-app-icon">{w.icon}</span>
            <span className="upi-app-name">{w.name}</span>
          </div>
        ))}
      </div>
      {sel && <div className="info-notice" style={{ marginTop:"1rem" }}><span>ℹ️</span><span>Redirecting to wallet to complete payment.</span></div>}
    </div>
  );
}

// ─── BOOKING REVIEW ───────────────────────────────────────────
function BookingReview({ ctx }) {
  const car  = ctx.selectedCar;
  const exts = (ctx.selectedExtras || []).filter(e => e.selected || e.id);
  return (
    <div className="s-card">
      <div className="s-title">Booking Review</div>
      <div style={{ marginBottom:"1rem" }}>
        <div style={{ fontSize:".68rem", letterSpacing:"1.5px", textTransform:"uppercase", color:"var(--gold)", fontWeight:700, marginBottom:".5rem" }}>Vehicle</div>
        <div className="review-row"><span className="review-key">Car</span><span className="review-val">{car?.car_name} ({car?.car_brand})</span></div>
        <div className="review-row"><span className="review-key">Category</span><span className="review-val">{car?.cat_name} · {car?.group_name}</span></div>
        <div className="review-row"><span className="review-key">Transmission</span><span className="review-val">{car?.car_transmission}</span></div>
        <div className="review-row"><span className="review-key">Passengers</span><span className="review-val">{car?.car_passgrno} seats</span></div>
      </div>
      <div style={{ marginBottom:"1rem" }}>
        <div style={{ fontSize:".68rem", letterSpacing:"1.5px", textTransform:"uppercase", color:"var(--gold)", fontWeight:700, marginBottom:".5rem" }}>Rental</div>
        <div className="review-row"><span className="review-key">Pickup</span><span className="review-val">{fmtDate(ctx.pickupDate)} at {ctx.pickupTime}</span></div>
        <div className="review-row"><span className="review-key">Return</span><span className="review-val">{fmtDate(ctx.returnDate)} at {ctx.returnTime}</span></div>
        <div className="review-row"><span className="review-key">Duration</span><span className="review-val">{ctx.days} day{ctx.days!==1?"s":""}</span></div>
        <div className="review-row"><span className="review-key">Location</span><span className="review-val">{ctx.pickup}</span></div>
      </div>
      {(exts.length > 0 || ctx.selectedInsurance) && (
        <div>
          <div style={{ fontSize:".68rem", letterSpacing:"1.5px", textTransform:"uppercase", color:"var(--gold)", fontWeight:700, marginBottom:".5rem" }}>Add-ons</div>
          <div className="extras-list">
            {exts.map(e => <span key={e.id} className="extra-pill">{e.icon||"➕"} {e.name}</span>)}
            {/* {ctx.selectedInsurance && <span className="extra-pill">🛡️ {ctx.selectedInsurance.ins_item}</span>} */}
            {(Array.isArray(ctx.selectedInsurance)
            ? ctx.selectedInsurance
            : ctx.selectedInsurance ? [ctx.selectedInsurance] : []
            ).map(ins => (
            <span key={ins.ins_id} className="extra-pill">🛡️ {ins.ins_item}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── COUPON ───────────────────────────────────────────────────
function CouponSection({ carId, onDiscount }) {
  const [code,    setCode   ] = useState("");
  const [status,  setStatus ] = useState(null);
  const [message, setMessage] = useState("");
  const [applied, setApplied] = useState(false);
  const today = new Date().toISOString().split("T")[0];

  const apply = async () => {
    if (!code.trim()) return;
    setStatus("loading"); setMessage("");
    try {
      const res  = await fetch(`${API_BASE}/coupons`);
      const list = await res.json();
      const match = Array.isArray(list)
        ? list.find(c => c.code_name?.toUpperCase() === code.trim().toUpperCase())
        : null;
      if (!match)                                      { setStatus("err"); setMessage("Invalid or expired coupon."); onDiscount(0); return; }
      if (match.code_cative !== 1 && match.code_cative !== "1") { setStatus("err"); setMessage("Coupon is inactive."); onDiscount(0); return; }
      if (match.from_date && today < match.from_date)  { setStatus("err"); setMessage(`Valid from ${fmtDate(match.from_date)}.`); onDiscount(0); return; }
      if (match.to_date   && today > match.to_date)    { setStatus("err"); setMessage(`Expired on ${fmtDate(match.to_date)}.`); onDiscount(0); return; }
      if (match.carmodel_id && match.carmodel_id !== 0 && carId && match.carmodel_id !== carId)
                                                       { setStatus("err"); setMessage("Not valid for your vehicle."); onDiscount(0); return; }
      const pct = parseFloat(match.discount) || 0;
      setStatus("ok"); setMessage(`${pct}% discount applied!`); setApplied(true); onDiscount(pct);
    } catch {
      setStatus("err"); setMessage("Could not verify coupon."); onDiscount(0);
    }
  };

  const remove = () => { setCode(""); setStatus(null); setMessage(""); setApplied(false); onDiscount(0); };

  return (
    <div className="s-card">
      <div className="s-title">Promo Code</div>
      <div className="coupon-row">
        <input className="coupon-input" placeholder="Enter promo code"
          value={code} disabled={applied}
          onChange={e => { setCode(e.target.value); if(status && status!=="loading"){setStatus(null);setMessage("");} }}
          onKeyDown={e => e.key==="Enter" && !applied && apply()} />
        {applied
          ? <button className="coupon-btn" style={{borderColor:"var(--red)",color:"var(--red)"}} onClick={remove}>Remove</button>
          : <button className="coupon-btn" onClick={apply} disabled={status==="loading"||!code.trim()}>{status==="loading"?"…":"Apply"}</button>}
      </div>
      {status==="loading" && <div className="coupon-loading">⏳ Checking…</div>}
      {status==="ok"      && <div className="coupon-success">✓ {message}</div>}
      {status==="err"     && <div className="coupon-error">✗ {message}</div>}
    </div>
  );
}

// ─── RIGHT PANEL ─────────────────────────────────────────────
function PaymentPanel({ totals, discountPct, processing, payMethod, onPay }) {
  const discountAmt  = totals.advance * (discountPct / 100);
  const finalAdvance = totals.advance - discountAmt;
  return (
    <aside className="pay-right">
      <div className="bp-section">
        <div className="bp-heading">Order Summary</div>
        <div className="price-row"><span className="price-label">Base rental</span><span className="price-val">{fmtPrice(totals.base)}</span></div>
        {totals.night > 0 && <div className="price-row"><span className="price-label">🌙 Night surcharge</span><span className="price-val">{fmtPrice(totals.night)}</span></div>}
        {totals.extras > 0 && <div className="price-row"><span className="price-label">Extras</span><span className="price-val">{fmtPrice(totals.extras)}</span></div>}
        {/* {totals.ins > 0    && <div className="price-row"><span className="price-label">Insurance</span><span className="price-val">{fmtPrice(totals.ins)}</span></div>} */}
        {(Array.isArray(totals.insArray) ? totals.insArray : []).map(ins => (
        <div key={ins.ins_id} className="price-row">
        <span className="price-label">🛡️ {ins.ins_item}</span>
        <span className="price-val">
        {fmtPrice(parseFloat(ins.ins_chargeperday || 0) * (totals.days || 1))}
        </span>
        </div>
        ))}
        {totals.insArray?.length === 0 && totals.ins === 0 ? null : null}

        <div className="price-row divider"><span className="price-label">Sub-total</span><span className="price-val">{fmtPrice(totals.sub)}</span></div>
        <div className="price-row"><span className="price-label" style={{fontSize:".76rem"}}>GST (18%)</span><span className="price-val" style={{fontSize:".8rem"}}>{fmtPrice(totals.tax)}</span></div>
        <div className="price-row grand"><span className="price-label">Grand Total</span><span className="price-val">{fmtPrice(totals.grand)}</span></div>
      </div>

      <div className="adv-highlight">
        <div className="adv-label">Pay Now (30% Advance)</div>
        {discountPct > 0 && <div style={{fontSize:".75rem",color:"var(--green)",marginBottom:".25rem"}}>Coupon: −{discountPct}% = −{fmtPrice(discountAmt)}</div>}
        <div className="adv-amount">{fmtPrice(finalAdvance)}</div>
        <div className="adv-sub">Secure your booking today</div>
        <div className="adv-balance">Balance at pickup: <strong>{fmtPrice(totals.balance + discountAmt)}</strong></div>
      </div>

      <div className="security-badges">
        <div className="sec-badge"><div className="sec-dot"/>SSL Encrypted</div>
        <div className="sec-badge"><div className="sec-dot"/>PCI-DSS</div>
        <div className="sec-badge"><div className="sec-dot"/>3D Secure</div>
      </div>

      <div style={{marginTop:"auto"}}>
        <button className={`btn-pay ${processing?"processing":""}`}
          disabled={!payMethod || processing} onClick={onPay}>
          {processing
            ? <><div className="spinner"/> Processing…</>
            : <>🔒 Pay {fmtPrice(finalAdvance)}</>}
        </button>
        <div className="secure-note">Free cancellation up to 24h · No hidden fees</div>
      </div>
    </aside>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────
export default function BookingStep4() {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Booking context — normalise personal info keys here ────
  const rawCtx = location.state || mockCtx;
  const [ctx, setCtx] = useState(rawCtx);

  const totals = useMemo(() => calcTotals(ctx), [ctx]);

  // ── ✅ FIX: normalise personal info from Step 3 regardless of key format ──
  const [personalInfo, setPersonalInfo] = useState(
    () => normalisePersonalInfo(rawCtx.personalInfo)
  );

  const [payTab,      setPayTab      ] = useState("card");
  const [payMethod,   setPayMethod   ] = useState(null);
  const [discountPct, setDiscountPct ] = useState(0);
  const [processing,  setProcessing  ] = useState(false);
  const [showEdit,    setShowEdit    ] = useState(false);

  const switchTab = tab => { setPayTab(tab); setPayMethod(null); };

  const handleSavePersonal = updated => {
    setPersonalInfo(updated);
    setCtx(prev => ({ ...prev, personalInfo: updated }));
    setShowEdit(false);
  };

  // ── Load Razorpay script once on mount ────────────────────────
useEffect(() => {
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.async = true;
  document.body.appendChild(script);
  return () => document.body.removeChild(script);
}, []);

// ── handlePay ─────────────────────────────────────────────────
const handlePay = async () => {
  if (!personalInfo.firstName?.trim() || !personalInfo.email?.trim()) {
    alert('Please complete your personal details.');
    setShowEdit(true);
    return;
  }

  setProcessing(true);

  try {
    const discountAmt  = totals.advance * (discountPct / 100);
    const finalAdvance = Math.round(totals.advance - discountAmt);
    const bookingRef   = 'BBI-' + Math.random().toString(36).slice(2, 8).toUpperCase();

    // 1️⃣ Fetch your Razorpay key from backend (never hardcode in React)
    // const keyRes = await fetch('http://localhost:5000/api/payment/key');
    // const { key } = await keyRes.json();
    // const keyRes = await fetch(
    //   'http://localhost:5000/api/payment/key'
    //   );
      const keyRes = await fetch(
        `${API_BASE}/payment/key`
      );
      
      if (!keyRes.ok) {
          throw new Error(
            `Key API failed: ${keyRes.status}`
          );
      }
      
      const keyData = await keyRes.json();
      
      if (!keyData.key) {
          throw new Error(
            "Razorpay key missing"
          );
      }
      
      const key = keyData.key;

    // 2️⃣ Create order on backend
    // const orderRes = await fetch('http://localhost:5000/api/payment/create-order', {
    //   method:  'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({
    //     amount:     finalAdvance,
    //     bookingRef,
    //     carName:    ctx.selectedCar?.car_name,
    //   }),
    // });

    const orderRes = await fetch(`${API_BASE}/payment/create-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        amount: finalAdvance,
        bookingRef,
        carName: ctx.selectedCar?.car_name,
      }),
    });

    if (!orderRes.ok) {
      const err = await orderRes.json();
      throw new Error(err.error || 'Order creation failed');
    }

    const { orderId, amount, currency } = await orderRes.json();

    // 3️⃣ Open Razorpay checkout popup
    const options = {
      key,
      amount,
      currency,
      name:        'YourFleet',
      description: `Booking — ${ctx.selectedCar?.car_name}`,
      order_id:    orderId,
      prefill: {
        name:    `${personalInfo.firstName} ${personalInfo.lastName}`,
        email:   personalInfo.email,
        contact: personalInfo.mobile,
      },
      notes: { bookingRef },
      theme: { color: '#C9A84C' }, // matches your gold theme

      handler: async (response) => {
        // 4️⃣ Verify signature on backend
        try {
          // const verifyRes = await fetch('http://localhost:5000/api/payment/verify', {
          //   method:  'POST',
          //   headers: { 'Content-Type': 'application/json' },
          //   body: JSON.stringify(response),
          // });
          const verifyRes = await fetch(`${API_BASE}/payment/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(response),
          });
          const { success, paymentId } = await verifyRes.json();

          if (success) {
            navigate('/booking-step5', {
              state: {
                ...ctx,
                personalInfo,
                paymentMethod: payTab,
                paymentStatus: 'success',
                bookingRef,
                paymentId,
                paidAmount:  finalAdvance,
                grandTotal:  totals.grand,
              },
            });
          } else {
            alert('Payment verification failed. Please contact support.');
          }
        } catch (err) {
          alert('Verification error: ' + err.message);
        } finally {
          setProcessing(false);
        }
      },

      modal: {
        ondismiss: () => {
          setProcessing(false); // user closed without paying
        },
      },
    };

    const rzp = new window.Razorpay(options);

    // Handle payment failure inside the popup
    rzp.on('payment.failed', (response) => {
      console.error('Payment failed:', response.error);
      alert(`Payment failed: ${response.error.description}`);
      setProcessing(false);
    });

    rzp.open();

  } catch (err) {
    console.error('handlePay error:', err);
    alert('Could not initiate payment: ' + err.message);
    setProcessing(false);
  }
};

  const PAY_TABS = [
    { id:"card",       icon:"💳", label:"Card"        },
    { id:"upi",        icon:"📱", label:"UPI"         },
    { id:"netbanking", icon:"🏦", label:"Net Banking" },
    { id:"wallet",     icon:"👛", label:"Wallet"      },
  ];

  return (
    <>
      <style>{css}</style>

      {processing && (
        <div className="processing-overlay">
          <div className="processing-ring"/>
          <div className="processing-text">Processing Payment</div>
          <div className="processing-sub">Please do not close this window…</div>
        </div>
      )}

      {showEdit && (
        <EditPersonalModal
          info={personalInfo}
          onSave={handleSavePersonal}
          onClose={() => setShowEdit(false)}
        />
      )}

      <StepNav onLogoClick={() => navigate("/")} />

      <div className="step4-wrap">
        <SummaryBar ctx={ctx} onEdit={() => navigate("/booking-step2", { state: ctx })} />

        <div className="step4-header">
          <h1>Secure Payment</h1>
          <p>Review your booking, verify your details, then choose a payment method.</p>
        </div>

        <div className="step4-layout">
          <div className="pay-left">
            {/* ✅ Shows real data from Step 3 */}
            <PersonalInfoCard info={personalInfo} onModify={() => setShowEdit(true)} />

            <BookingReview ctx={ctx} />

            <CouponSection carId={ctx.selectedCar?.car_id} onDiscount={setDiscountPct} />

            <div className="s-card">
              <div className="s-title">Payment Method</div>
              <div className="pay-tabs">
                {PAY_TABS.map(t => (
                  <div key={t.id} className={`pay-tab ${payTab===t.id?"active":""}`} onClick={() => switchTab(t.id)}>
                    <span className="pay-tab-icon">{t.icon}</span>
                    <span className="pay-tab-label">{t.label}</span>
                  </div>
                ))}
              </div>
              {payTab === "card"       && <CardForm       onCardChange={setPayMethod}  />}
              {payTab === "upi"        && <UpiForm         onReady={setPayMethod}       />}
              {payTab === "netbanking" && <NetBankingForm  onReady={setPayMethod}       />}
              {payTab === "wallet"     && <WalletForm      onReady={setPayMethod}       />}
            </div>
          </div>

          <PaymentPanel
            totals={totals}
            discountPct={discountPct}
            processing={processing}
            payMethod={payMethod}
            onPay={handlePay}
          />
        </div>
      </div>
    </>
  );
}